// The Compensation tab's words (lane B8 S13): the offer package card with the six rows of BRD chapter 6 in his order,
// the attribute chips, the prepared fields, the conditional approval rows and the readiness gate. Every entry quotes
// the line of his glossary (or of the design-system excerpts) it is taken from; labels.test.mjs checks the quote, the
// text and the status. The shared words (Prepare, Approve, Extend verbal offer, the package chain, the roles,
// Organizational leader, Total Rewards, Evidence, Offer readiness, Limited, Notes, Required) come from @/mc.

import { defineLabels, type LabelDef, type LabelSource } from "@/mc/labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const satisfies LabelDef;
const pending = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "pending" }) as const satisfies LabelDef;

const ROW1 = "| Recommendation / recommended hiring range | Row 1: HM \"Yes\" · Recruiter/Coordinator/HRBP/Compensation \"Full\" · Interviewer \"No\".";
const ROW2 = "| Proposed base / bonus / commission | Row 2: HM Yes · others Full · Interviewer No.";
const ROW3 = "| Sign-on / relocation / LTI request and status | Row 3: HM Yes · others Full · Interviewer No.";
const ROW4 = "| Salary grade | Row 4: HM No · others Full · Interviewer No.";
const ROW5 = "| Market data / internal range / positioning | Row 5: HM No · others Full · Interviewer No.";
const ROW6 = "| Internal equity / working notes / approval evidence | Row 6: HM \"No or own-action only\" · others Full · Interviewer No.";
const HM_LIMITED = "\"HM limited to recommendation/range, proposed base, bonus, commission, requests/status/final terms. Interviewer none.\"";
const FINAL_TERMS = "| Final terms | \"requests/status/final terms\" (HM may see). |";
const PACKAGE = "\"Offer package and conditional approvals\"";
const STANDARD = "\"Standard offer requires HM + HRBP.\"";
const SIGN_ON = "\"Sign-on or approved relocation: organizational leader approval obtained by HM, then HM + HRBP.\"";
const VP_LTI = "\"VP+ LTI: Total Rewards coordination/approved amount/wording, then HM + HRBP.\"";
const READINESS = "\"readiness lists complete/missing approvals.\"";
const START = "\"Acceptance to Start = accepted to start date.\"";
const APPROVAL_ROW = "*HM* · *HRBP* · *Organizational leader* (obtained by HM) · *Total Rewards* · *Pending · Approved · Rejected · Revision Required* · *Evidence*";

/** The six §6 rows, in his order (row 1 first). */
export const PKG_ROW_LABELS = /* @__PURE__ */ defineLabels({
  row1: his("Recommendation / recommended hiring range"),
  row2: his("Proposed base / bonus / commission"),
  row3: his("Sign-on / relocation / LTI request and status"),
  row4: his("Salary grade"),
  row5: his("Market data / internal range / positioning"),
  row6: his("Internal equity / working notes / approval evidence"),
});

/** The card's title, the approvals panel's title, the attribute chips and the HM's two extra lines. */
export const PKG_LABELS = /* @__PURE__ */ defineLabels({
  offerPackage: his("Offer package"),
  conditionalApprovals: his("Conditional approvals"),
  standardOffer: his("Standard offer"),
  signOn: his("Sign-on"),
  relocation: his("Relocation"),
  lti: his("LTI"),
  status: his("Status"),
  finalTerms: his("Final terms"),
  /** The HM's own act in row 6 (his access word for that cell). */
  ownAction: his("Own-action only"),
});

/** The prepared fields (the Recruiter's form) and the Total Rewards row's two fields. */
export const PKG_FIELD_LABELS = /* @__PURE__ */ defineLabels({
  proposedBase: his("Proposed base"),
  bonus: his("Bonus"),
  commission: his("Commission"),
  signOn: his("Sign-on"),
  relocation: his("Relocation"),
  lti: his("LTI"),
  wording: his("Wording"),
  approvedAmount: his("Approved amount"),
  startDate: his("Start date"),
});

/** The approval rows' state words (DS row 116). */
export const APPROVAL_STATE_LABELS = /* @__PURE__ */ defineLabels({
  pending: his("Pending"),
  approved: his("Approved"),
  rejected: his("Rejected"),
  revisionRequired: his("Revision Required"),
});

/** The readiness rows' two words. */
export const READINESS_LABELS = /* @__PURE__ */ defineLabels({
  complete: his("Complete"),
  missing: his("Missing"),
});

/** His rule sentences on the tab. */
export const PKG_RULES = /* @__PURE__ */ defineLabels({
  blockVerbal: his("Block verbal offer until all applicable approvals complete."),
  invalidates: his("Package changes invalidate affected approvals."),
  standard: his("Standard offer requires HM + HRBP."),
  signOn: his("Sign-on or approved relocation: organizational leader approval obtained by HM, then HM + HRBP."),
  vpLti: his("VP+ LTI: Total Rewards coordination/approved amount/wording, then HM + HRBP."),
});

/**
 * The Draft -> Pending Approvals control (plan D15). He names the transition, not the control; the control is his
 * verb for sending a record into review, *Submit* (G §17: "Submit (slate / scorecard / request)"), so no word is
 * coined for it.
 */
export const PKG_SEND_LABELS = /* @__PURE__ */ defineLabels({
  send: his("Submit"),
});
